import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,  Result, SectionTitle, Image, Separator,CorrectionBox,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider} from '../../../../components/course/CourseComponents';

const Chapitre1Content = ({ courseId = 'monpremierjeu' }) => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Dans ce chapitre, nous allons créer le squelette de notre jeu Flappy Bird et apprendre à charger et afficher une image de fond.
        </p>
      </InfoBox>      
     
      <Separator type="text" text="Création du squelette" color="gray-darker" size="md" />
      <SectionTitle>🎮 1/ Squelette du jeu</SectionTitle>

      <p>Commençons par écrire le squelette de notre jeu.</p>
      <p>Comme pour les jeux précédents, on aura besoin des fonctions <InlineCode>setup</InlineCode> pour créer un canvas, <InlineCode>draw</InlineCode> pour afficher notre jeu, et <InlineCode>mousePressed</InlineCode> pour détecter les clics du joueur.</p>

      <p>On va ajouter une nouvelle fonction <InlineCode>preload</InlineCode> qui est appelée avant que le jeu débute, c'est-à-dire avant <InlineCode>setup</InlineCode> et <InlineCode>draw</InlineCode>. Elle servira à charger les images et les sons du jeu.</p>

      <Note type="info" title="💡 Pourquoi dans preload et pas setup ?">
        <p><InlineCode>preload()</InlineCode> est une fonction spéciale de p5.js qui est appelée avant <InlineCode>setup()</InlineCode>.</p>
        <ul className="list-disc list-inside mb-4">
          <li>Elle est utilisée pour précharger des fichiers (images, sons, polices, etc.) de manière synchronisée.</li>
          <li>Le programme attend que tous les fichiers dans <InlineCode>preload()</InlineCode> soient chargés avant d'exécuter <InlineCode>setup()</InlineCode>.</li>
          <li>Si tu utilises <InlineCode>loadImage()</InlineCode> dans <InlineCode>setup()</InlineCode> ou <InlineCode>draw()</InlineCode>, l'image peut ne pas être encore disponible quand tu essaies de l'afficher, ce qui peut causer des bugs ou afficher une image vide.</li>
        </ul>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : Création du squelette">
        <p>Ajoute les fonctions <InlineCode>preload</InlineCode>, <InlineCode>setup</InlineCode>, <InlineCode>draw</InlineCode>, et <InlineCode>mousePressed</InlineCode>.</p>
        <p>Ajoute <InlineCode>createCanvas(600, 600)</InlineCode> dans <InlineCode>setup</InlineCode>.</p>

        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird

//--------------------------------- PRELOAD ------------------------------------------
function preload() {

}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {

}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {

}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Gestion des images" color="gray-darker" size="md" />
      <SectionTitle>🖼️ 2/ Charger une image et la mettre en fond</SectionTitle>

      <p>Comme on aura besoin d'images et de sons pour ce projet, je t'ai ajouté un outil qui affiche tous les assets dont tu auras besoin.</p>
      <p>Clique sur le bouton violet, sous le bouton Play.</p>

      <p>Pour charger une image, on aura besoin de la fonction p5.js suivante :</p>
      
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>loadImage(nom)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Charge une image en mémoire. À mettre dans la fonction preload.<br/>- Paramètre : nom de l'image (sous forme de texte)<br/>- Retourne une référence vers cette image</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>Cette fonction permet de charger une image en mémoire et de la stocker dans une variable.</p>
      <p>Il faut appeler cette fonction dans <InlineCode>preload</InlineCode> :</p>

      <CodeBlock language="javascript">
{`imageFond = loadImage("fond.png");`}
      </CodeBlock>

      <p>Pour afficher une image, on utilise la fonction <InlineCode>image</InlineCode> ou <InlineCode>background</InlineCode> de p5.js :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>image(img, x, y)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche l'image à la position (x, y) dans le canvas, à sa taille originale.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>image(img, x, y, width, height)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche l'image à (x, y) mais redimensionnée.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>background(img)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche l'image en fond (habituellement on spécifie une couleur RVB, mais ici on utilise une image).</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <CodeBlock language="javascript">
{`background(imageFond);`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Chargement et affichage du fond">
          - En global, crée une variable <InlineCode>imageFond</InlineCode>.<br/>
          - Dans la fonction <InlineCode>preload</InlineCode>, charge l'image <InlineCode>fond.png</InlineCode>:<br/>
          Pour gagner du temps, ouvre l'Asset Manager en cliquant sur l'icône violet. Puis clique sur <InlineCode>loadImage</InlineCode> de l'image <InlineCode>fond.png</InlineCode>. Tu aura la bonnne fonction directement dans le presse-papiers.<br/>
          - Assigne cette image à la variable <InlineCode>imageFond</InlineCode>. <br/>
          - Dans <InlineCode>draw</InlineCode>, appelle la fonction <InlineCode>background</InlineCode> avec <InlineCode>imageFond</InlineCode>.
        <br/><br/>
        <Result>Tu devrais voir une fenêtre carrée avec une image en fond !</Result>

        <CorrectionBox>
          <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {

}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 3/ Correction : squelette du jeu et affichage du fond</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {

}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre1Content; 